import { withApollo } from "@apollo/client/react/hoc";
import { isEmpty } from "lodash";
import PropTypes from "prop-types";
import React from "react";
import AutoComplete from "react-google-autocomplete";
import { Link } from "react-router-dom";
// @material-ui/core components
import CircularProgress from "@mui/material/CircularProgress";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { withStyles } from "@mui/styles";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  Button,
  Card,
  CardBody,
  GridContainer,
  GridItem,
} from "~/shared/components/MuiComponents.jsx";

import imgSleepTrial from "~assets/images/visits/sleep-bottles.jpg";
import imgTwoPack from "~assets/images/visits/two-pack.png";

import { kickBlue, kickRed } from "~/shared/styles/baseStyles.js";
import consultationStyles from "../styles/consultationStyles";

import { onPlaceSelected } from "~/shared/utils/pharmacyPickerUtils";

import KickClient from "../utils/api.js";

import {
  trackVisitPharmacySelected,
  trackVisitProductSelected,
} from "../utils/analytics";
import {
  clearIsEditingPendingVisitPharmacy,
  clearMailOrderOnlyAddonsForConsultationType,
  clearOrderItemSubcategory,
  clearShipmentMethod,
  getActiveSubscriptionBySubType,
  getConsultAnswersJSON,
  getIsEditingPendingVisitPharmacy,
  getIsPickup,
  getVisitDifferentPharmacyJSON,
  getVisitID,
  getVisitProduct,
  isFollowUpVisit,
  setConsultationTypeOrderItem,
  setIsPickup,
  setVisitDifferentPharmacyJSON,
  setVisitProduct,
} from "../utils/visitCookies";

import { getMedicationFormPreference } from "~/shared/utils/sleepFlavorPreference";

import { checkAndRedirectToFollowUpIfNeeded } from "../utils/visitUtils";

import {
  COLOR_KICK_BLUE,
  COLOR_KICK_LIGHT_RED,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  ORDER_TYPE_PROP_VISIT_ONLY,
  ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP,
  ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP,
  ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
  PHARMACY_KICK_MAIL_ORDER,
  SUBSCRIPTION_SUB_TYPE_SLEEP,
} from "~/shared/sharedConstants";
import {
  formatPhoneNumber,
  getGoogleAPIKey,
  isConsultationTypeInsomnia,
  isConsultationTypeInsomniaFollowUp,
  isConsultationTypeInsomniaInitial,
  isConsultationTypePerformanceAnxietyFollowUp,
  isConsultationTypePerformanceAnxietyInitial,
  isPharmacyPickupSleepOrderType,
} from "~/shared/utils/sharedUtils";

class SelectPharmacy extends React.Component {
  constructor(props) {
    super(props);
    const queryParams = new URLSearchParams(this.props.location.search);
    const checkoutSessionID = queryParams.get("checkoutSessionID");
    const pharmacyJSON = getVisitDifferentPharmacyJSON(checkoutSessionID) || {};
    this.state = {
      consultationType: this.props.match.params.consultationType,
      checkoutSessionID: checkoutSessionID,
      useDifferentPharmacy: getIsPickup() || !isEmpty(pharmacyJSON),
      showSelectedPharmacy: !isEmpty(pharmacyJSON),
      selectedSearchResult: null,
      ...(pharmacyJSON || {}),
      loadingSearchResults: false,
      autocompleteValue: "",
    };

    if (!getVisitID(checkoutSessionID)) {
      window.location.href = "/me/portal";
    }
  }

  confirmDelivery = () => {
    const { checkoutSessionID } = this.state;
    setVisitDifferentPharmacyJSON("", checkoutSessionID);
    trackVisitPharmacySelected(
      getVisitID(checkoutSessionID),
      PHARMACY_KICK_MAIL_ORDER,
    );

    if (
      isConsultationTypePerformanceAnxietyInitial(
        this.state.consultationType,
      ) ||
      isConsultationTypePerformanceAnxietyFollowUp(this.state.consultationType)
    ) {
      // performance anxiety visit - need to select one-pack or two-pack
      this.props.history.push(
        `/visits/${this.state.consultationType}/select-product?checkoutSessionID=${this.state.checkoutSessionID}`,
      );
    } else {
      if (isConsultationTypeInsomnia(this.state.consultationType)) {
        let currentOrderType = getVisitProduct(this.state.checkoutSessionID);
        // if order type is not set yet, or is a pharmacy pickup order type, default to mail order quarterly order type
        if (
          currentOrderType == null ||
          currentOrderType == "" ||
          isPharmacyPickupSleepOrderType(currentOrderType)
        ) {
          setVisitProduct(
            ORDER_TYPE_SLEEP_MEMBERSHIP_MAIL_ORDER_MONTHLY,
            this.state.checkoutSessionID,
          );
          setConsultationTypeOrderItem(
            this.state.consultationType,
            getVisitProduct(this.state.checkoutSessionID),
            this.state.checkoutSessionID,
          );
        }

        // Skip the flavor picker if the intake already established the patient
        // doesn't want gummies. Clear any stale subcategory cookie so it doesn't
        // override their stated preference at checkout.
        const medicationFormPreference = getMedicationFormPreference(
          getConsultAnswersJSON(),
        );
        if (
          medicationFormPreference &&
          medicationFormPreference !== "gummy"
        ) {
          clearOrderItemSubcategory(
            this.state.consultationType,
            this.state.checkoutSessionID,
          );
          this.props.history.push(
            `/visits/${this.state.consultationType}/payment/order-details?checkoutSessionID=${this.state.checkoutSessionID}`,
          );
        } else {
          this.props.history.push(
            `/visits/${this.state.consultationType}/select-sleep-flavored?checkoutSessionID=${this.state.checkoutSessionID}`,
          );
        }
      }
    }

    window.scrollTo(0, 0);
  };

  isSelected = (index) => {
    return this.state.selectedIndex === index;
  };

  toggleDifferentPharmacy = (e) => {
    e.preventDefault();
    setIsPickup(!this.state.useDifferentPharmacy);
    this.setState({ useDifferentPharmacy: !this.state.useDifferentPharmacy });
  };

  setVisitProductToPickUp() {
    const { checkoutSessionID, consultationType } = this.state;
    clearShipmentMethod(checkoutSessionID);
    clearMailOrderOnlyAddonsForConsultationType(
      consultationType,
      checkoutSessionID,
    );

    if (
      // insomnia visit - set to monthly pickup membership
      isConsultationTypeInsomniaInitial(consultationType)
    ) {
      // will need to expand this later if we add more options to payment checkout screen
      setVisitProduct(
        ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_MONTHLY,
        checkoutSessionID,
      );
    } else if (isConsultationTypeInsomniaFollowUp(consultationType)) {
      setVisitProduct(
        ORDER_TYPE_SLEEP_MEMBERSHIP_VISIT_ONLY_FOLLOW_UP,
        checkoutSessionID,
      );
    } else {
      // BB visit - need to differentiate between follow-up and initial
      if (
        isFollowUpVisit(checkoutSessionID) ||
        isConsultationTypePerformanceAnxietyFollowUp(consultationType)
      ) {
        setVisitProduct(
          ORDER_TYPE_PROP_VISIT_ONLY_FOLLOW_UP,
          checkoutSessionID,
        );
      } else {
        // default fallback - set to pick-up initial BB visit
        setVisitProduct(ORDER_TYPE_PROP_VISIT_ONLY, checkoutSessionID);
      }
    }

    setConsultationTypeOrderItem(
      this.state.consultationType,
      getVisitProduct(checkoutSessionID),
      checkoutSessionID,
    );
  }

  renderDetailsByVisitType() {
    // console.log("consultationType: " + this.state.consultationType);
    switch (this.state.consultationType) {
      case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY:
      case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP:
        return this.renderPerformanceAnxietyDetails();

      case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA:
      case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP:
        return this.renderInsomniaDetails();

      default:
        return this.renderPerformanceAnxietyDetails();
    }
  }

  renderInsomniaDetails() {
    // set the visit product here since we don't select product like for BBs

    return (
      <React.Fragment>
        <div
          style={{
            margin: "10px auto",
          }}
        >
          <h3
            style={{
              width: "300px",
              textAlign: "center",
            }}
          >
            Direct Mail Pharmacy
          </h3>

          <ul
            style={{
              width: "330px",
              margin: "16px 0 10px 0",
              position: "relative",
            }}
          >
            <strong>
              <li>Bonus Sleep Tracking Journal</li>
              <li>Free Shipping Directly To Your Bedside</li>
            </strong>
            <li>Convenient packaging</li>
            <li style={{ marginTop: "2px" }}>Easy refills when needed</li>
            <li>Access to our fast-acting flavored sleep gummies</li>
          </ul>
        </div>

        <img
          src={imgSleepTrial}
          style={{
            width: "316px",
            margin: "0 auto 40px auto",
          }}
          alt="Sleep Medication Bottle"
        />

        <Link
          to="#"
          onClick={this.toggleDifferentPharmacy}
          style={{
            margin: "0 auto 8px auto",
            display: "block",
            textAlign: "center",
            // color: "white"
          }}
        >
          Manually enter a pick-up pharmacy
        </Link>
      </React.Fragment>
    );
  }

  renderPerformanceAnxietyDetails() {
    return (
      <React.Fragment>
        <div
          style={{
            margin: "10px auto",
            // border: "1px solid pink",
          }}
        >
          <h3
            style={{
              // width: "300px",
              textAlign: "center",
              // border: "1px solid green",
              marginBottom: "4px",
            }}
          >
            Kick's Online Pharmacy
          </h3>

          <p
            style={{
              marginTop: 0,
              textAlign: "center",
              fontWeight: "bold",
              // border: "2px solid yellow",
              color: kickRed,
              fontSize: "16px",
            }}
          >
            Exclusive Custom Matchbox Packaging <br />
            22% lifetime discount on all future refills
          </p>

          <ul
            style={{
              width: "330px",
              margin: "16px 0 10px 0",
              position: "relative",
            }}
          >
            <li style={{ marginTop: "6px" }}>Easy One-Click Refills</li>
            <li>FREE Shipping</li>
            <li>No Monthly Subscriptions</li>
            <li>Access to our fast-acting flavored Beta-blocker mints</li>
          </ul>
        </div>

        <img
          src={imgTwoPack}
          style={{
            width: "426px",
            maxWidth: "100%",
            margin: "0 auto 30px auto",

            position: "relative",
            left: "50px",
            // border: "1px solid red"
          }}
          alt="Two Pack Propranolol Tablets"
        />
        <Link
          to="#"
          onClick={this.toggleDifferentPharmacy}
          style={{
            margin: "0 auto 8px auto",
            display: "block",
            textAlign: "center",
            // color: "white"
          }}
        >
          Manually Enter a Pickup Pharmacy
        </Link>
      </React.Fragment>
    );
  }

  renderUseKickPharmacy() {
    const { classes } = this.props;
    if (!this.state.useDifferentPharmacy) {
      return (
        <React.Fragment>
          <Card className={classes.cardConsultation}>
            <CardBody
              style={{
                display: "flex",
                flexDirection: "column",
                padding: "0 2px",
                overflow: "hidden",
              }}
            >
              {this.renderDetailsByVisitType()}
            </CardBody>
          </Card>
          <Button
            onClick={this.confirmDelivery}
            style={{
              display: "block",
              backgroundColor: "#F25930",
              margin: "30px auto",
              width: "300px",
              fontSize: "18px",
              fontWeight: "medium",
              textTransform: "unset",
            }}
          >
            Confirm
          </Button>
        </React.Fragment>
      );
    }
  }

  renderAutocomplete() {
    const allowSwitchToMailOrder =
      !getIsEditingPendingVisitPharmacy(this.state.checkoutSessionID) &&
      (!isConsultationTypeInsomniaFollowUp(this.state.consultationType) ||
        (isConsultationTypeInsomniaFollowUp(this.state.consultationType) &&
          !getActiveSubscriptionBySubType(SUBSCRIPTION_SUB_TYPE_SLEEP)));
    return (
      <>
        <h4
          style={{
            width: "80%",
            margin: "0 auto 16px",
            textAlign: "center",
          }}
        >
          {
            "Enter your pharmacy info below. We can electronically send your prescription to the pharmacy."
          }
        </h4>
        {allowSwitchToMailOrder && (
          <div
            style={{
              borderRadius: "12px",
              padding: "8px 12px",
              margin: "0 4px 16px",
              textAlign: "center",
              backgroundColor: COLOR_KICK_LIGHT_RED,
              alignSelf: "center",
            }}
          >
            <p
              style={{
                fontSize: "14px",
                color: "#555",
                margin: "0 0 4px",
              }}
            >
              **Our fast-acting flavored meds are only available through the
              mail-order pharmacy
            </p>
            <Link
              to="#"
              style={{
                fontSize: "14px",
              }}
              onClick={this.toggleDifferentPharmacy}
            >
              Switch to mail-order
              <NavigateNextIcon
                style={{
                  verticalAlign: "middle",
                  fontSize: "15px",
                  marginLeft: "-2px",
                }}
              />
            </Link>
          </div>
        )}
        <div
          style={{
            width: "320px",
            margin: "0 auto 10px auto",
          }}
        >
          <label
            style={{
              display: "block",
              marginBottom: "6px",
              marginTop: "12px",
              color: "rgba(0, 0, 0, 0.87)",
              fontSize: "16px",
            }}
          >
            Find your pharmacy
          </label>
          <AutoComplete
            value={this.state.autocompleteValue}
            onChange={(e) => {
              this.setState({ autocompleteValue: e.target.value });
            }}
            placeholder="Walgreens, CVS, etc."
            style={{
              width: "100%",
              marginBottom: "20px",
              padding: "10px",
              border: "1px solid #999",
              borderRadius: "2px",
              fontSize: "16px",
            }}
            apiKey={getGoogleAPIKey()}
            onPlaceSelected={(place) => {
              onPlaceSelected(
                place,
                (autocompleteValue) => {
                  this.setState({ autocompleteValue });
                },
                (autocompleteErrorMsg) => {
                  this.setState({ autocompleteErrorMsg });
                },
                (pharmacySearchResults) => {
                  this.setState({ pharmacySearchResults });
                },
                (loadingSearchResults) => {
                  this.setState({ loadingSearchResults });
                },
                (searchParams) => {
                  this.setState({
                    loadingSearchResults: false,
                    showSelectedPharmacy: true,
                    ...searchParams,
                    pharmacyNCPDPID: "",
                    autocompleteValue: "",
                  });
                },
              );
            }}
            options={{
              types: ["establishment"],
              fields: ["name", "address_components", "formatted_phone_number"],
              componentRestrictions: { country: "us" },
            }}
          />
          {this.state.autocompleteErrorMsg && (
            <div style={{ color: kickRed, textAlign: "center" }}>
              {this.state.autocompleteErrorMsg}
            </div>
          )}
        </div>
        {allowSwitchToMailOrder && (
          <Link
            to="#"
            onClick={this.toggleDifferentPharmacy}
            style={{
              margin: "0 auto 16px auto",
              display: "block",
              textAlign: "center",
              fontSize: "17px",
              padding: "0 12px",
            }}
          >
            Skip the Trip...
            <br />
            Have medications mailed to you
            <NavigateNextIcon
              style={{
                verticalAlign: "middle",
                fontSize: "18px",
                marginLeft: "-2px",
              }}
            />
          </Link>
        )}
      </>
    );
  }

  getPharmacyDisabledError(pharmacy) {
    const disabledPharmacies = {
      riteAid: {
        matches: ["rite-aid", "rite aid"],
        errorMessage:
          "⚠️ Rite-Aid is in the process of closing down most of their retail locations. Please select another pharmacy.",
      },
    };
    const pName = pharmacy?.businessName?.toLowerCase();
    let errorMessage = "";
    Object.keys(disabledPharmacies).forEach((disabledPharm) => {
      if (
        disabledPharmacies[disabledPharm].matches.some((match) =>
          pName?.includes(match),
        )
      ) {
        errorMessage = disabledPharmacies[disabledPharm].errorMessage;
      }
    });
    return errorMessage;
  }

  renderScriptsureResults() {
    return (
      <div
        style={{
          margin: "0 20px",
        }}
      >
        <h3
          style={{
            color: kickBlue,
            fontFamily: "Spectral-SemiBold",
            margin: "10px",
          }}
        >
          Please confirm your selection
        </h3>
        <RadioGroup
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            margin: "auto",
          }}
          value={this.state.selectedSearchResult}
          onChange={(e) => {
            this.setState({ selectedSearchResult: Number(e.target.value) });
          }}
        >
          {this.state.pharmacySearchResults
            .slice(
              0,
              this.state.loadMore ? this.state.pharmacySearchResults.length : 6,
            )
            .map((pharmacy, pIdx) => {
              const disabledPharmacyError =
                this.getPharmacyDisabledError(pharmacy);
              return (
                <div
                  key={"pharm-" + pIdx}
                  onClick={() => {
                    if (!disabledPharmacyError) {
                      this.setState({ selectedSearchResult: pIdx });
                    }
                  }}
                  style={{
                    border: `1px solid #ccc`,
                    borderColor:
                      this.state.selectedSearchResult === pIdx
                        ? kickBlue
                        : "#ccc",
                    borderRadius: "8px",
                    padding: "10px",
                    color: kickBlue,
                    margin: "10px",
                    fontSize: "15px",
                    width: "320px",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      opacity: disabledPharmacyError ? 0.5 : 1,
                    }}
                  >
                    <Radio value={pIdx} disabled={!!disabledPharmacyError} />
                    <div style={{ padding: "0 10px" }}>
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: "bold",
                          marginBottom: "3px",
                        }}
                      >
                        {pharmacy.businessName}
                      </div>
                      <div>
                        {`${pharmacy.addressLine1} ${pharmacy.addressLine2}`.trim()}
                      </div>
                      <div>
                        {pharmacy.city}, {pharmacy.stateProvince}{" "}
                        {pharmacy.postalCode.slice(0, 5)}
                      </div>
                      <div>{formatPhoneNumber(pharmacy.primaryTelephone)}</div>
                    </div>
                  </div>
                  {disabledPharmacyError ? (
                    <div
                      style={{
                        backgroundColor: COLOR_KICK_LIGHT_RED,
                        border: "1px solid #f44336",
                        borderRadius: "4px",
                        padding: "8px",
                        marginTop: "12px",
                        fontSize: "13px",
                        color: "#d32f2f",
                        lineHeight: "1.4",
                      }}
                    >
                      {disabledPharmacyError}
                    </div>
                  ) : null}
                </div>
              );
            })}
          {/* {!this.state.loadMore &&
            this.state.pharmacySearchResults.length > 6 && (
              <a onClick={() => this.setState({ loadMore: true })}>
                Load more results
              </a>
            )} */}
          {!this.state.pharmacySearchResults.length && (
            <div
              style={{
                fontSize: "16px",
                margin: "20px auto",
                textAlign: "center",
              }}
            >
              No results found
            </div>
          )}
        </RadioGroup>

        <Link
          to="#"
          onClick={() =>
            this.setState({
              pharmacySearchResults: null,
              selectedSearchResult: null,
              loadMore: false,
            })
          }
          style={{
            margin: "24px auto 0",
            display: "block",
            textAlign: "center",
            fontSize: "18px",
          }}
        >
          {"< "}Go Back
        </Link>
      </div>
    );
  }

  enableConfirmDifferentPharmButton() {
    if (
      this.state.pharmacySearchResults &&
      this.state.selectedSearchResult !== null
    ) {
      return true;
    }
    if (
      this.state.showSelectedPharmacy &&
      !this.state.pharmacySearchResults &&
      !this.state.autocompleteValue
    ) {
      return true;
    }
    return false;
  }

  renderDifferentPharmacy() {
    const { classes } = this.props;

    if (this.state.useDifferentPharmacy) {
      return (
        <React.Fragment>
          <div className="choose-local-pharmacy">
            <Card className={classes.cardConsultation}>
              <CardBody
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "0 2px",
                }}
              >
                {this.renderSelectedPharmacy()}
                {!this.state.pharmacySearchResults
                  ? this.renderAutocomplete()
                  : this.renderScriptsureResults()}
              </CardBody>
            </Card>
            <Button
              onClick={() => {
                let pharmValues = this.getSelectedPharmacyFromState();
                if (this.state.selectedSearchResult !== null) {
                  const selectedPharmacy =
                    this.state.pharmacySearchResults[
                      this.state.selectedSearchResult
                    ];

                  pharmValues = {
                    pharmacyName: selectedPharmacy.businessName,
                    pharmacyAddress1: selectedPharmacy.addressLine1,
                    pharmacyAddress2: selectedPharmacy.addressLine2,
                    pharmacyCity: selectedPharmacy.city,
                    pharmacyState: selectedPharmacy.stateProvince,
                    pharmacyZip: selectedPharmacy.postalCode.slice(0, 5),
                    pharmacyPhone: selectedPharmacy.primaryTelephone,
                    pharmacyNCPDPID: selectedPharmacy.ncpdpId || "",
                  };
                }

                // if we are editing a pending visit pharmacy, save the pharmacy info and redirect back to portal
                if (
                  getIsEditingPendingVisitPharmacy(this.state.checkoutSessionID)
                ) {
                  KickClient.newAuthenticatedClient()
                    .put(
                      `/visits/${getVisitID(
                        this.state.checkoutSessionID,
                      )}/pharmacy-info`,
                      JSON.stringify(pharmValues),
                    )
                    .then(() => {
                      clearIsEditingPendingVisitPharmacy(
                        this.state.checkoutSessionID,
                      );
                      window.location.href = `/me/portal`;
                    })
                    .catch((err) => {
                      const errMsg =
                        err?.response?.data?.error ||
                        "An error occurred. Please try again or contact support.";
                      alert(errMsg);
                    });
                  return;
                }

                this.setVisitProductToPickUp();
                setVisitDifferentPharmacyJSON(
                  pharmValues,
                  this.state.checkoutSessionID,
                );
                trackVisitProductSelected(
                  getVisitID(this.state.checkoutSessionID),
                  getVisitProduct(this.state.checkoutSessionID),
                );
                trackVisitPharmacySelected(
                  getVisitID(this.state.checkoutSessionID),
                  pharmValues.pharmacyName,
                  pharmValues,
                );

                setConsultationTypeOrderItem(
                  this.state.consultationType,
                  getVisitProduct(this.state.checkoutSessionID),
                  this.state.checkoutSessionID,
                );

                // TODO: remove this once we have scopolamine upsell ready
                const goToScopolamineUpsell = false;
                // const goToScopolamineUpsell = [
                //   CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
                //   CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
                // ].includes(this.state.consultationType);

                const hasActiveSleepSubscription =
                  getActiveSubscriptionBySubType(SUBSCRIPTION_SUB_TYPE_SLEEP);

                if (goToScopolamineUpsell) {
                  this.props.history.push(
                    `/visits/${this.state.consultationType}/sca?checkoutSessionID=${this.state.checkoutSessionID}`,
                  );
                } else if (
                  isConsultationTypeInsomniaFollowUp(
                    this.state.consultationType,
                  ) &&
                  hasActiveSleepSubscription
                ) {
                  // If INF with active subscription, save the pharmacy info
                  const pharmacyInfo = JSON.stringify(pharmValues);
                  KickClient.newAuthenticatedClient()
                    .put(
                      `/visits/${getVisitID(
                        this.state.checkoutSessionID,
                      )}/pharmacy-info`,
                      pharmacyInfo,
                    )
                    .then(() => {
                      this.props.history.push(
                        `/visits/${this.state.consultationType}/complete?checkoutSessionID=${this.state.checkoutSessionID}`,
                      );
                    });
                } else {
                  this.props.history.push(
                    `/visits/${this.state.consultationType}/payment/order-details?checkoutSessionID=${this.state.checkoutSessionID}`,
                  );
                }
              }}
              style={{
                display: "block",
                backgroundColor: "#F25930",
                margin: "30px auto",
                width: "300px",
                fontSize: "18px",
                fontWeight: "medium",
                textTransform: "unset",
              }}
              disabled={!this.enableConfirmDifferentPharmButton()}
            >
              Confirm
            </Button>
          </div>
        </React.Fragment>
      );
    }
  }

  getSelectedPharmacyFromState() {
    const {
      pharmacyName,
      pharmacyAddress1,
      pharmacyAddress2,
      pharmacyCity,
      pharmacyState,
      pharmacyZip,
      pharmacyPhone,
      pharmacyNCPDPID,
    } = this.state;
    return {
      pharmacyName,
      pharmacyAddress1,
      pharmacyAddress2,
      pharmacyCity,
      pharmacyState,
      pharmacyZip,
      pharmacyPhone,
      pharmacyNCPDPID,
    };
  }

  renderSelectedPharmacy() {
    return (
      this.state.showSelectedPharmacy &&
      !this.state.pharmacySearchResults && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <h3
            style={{
              fontFamily: "Spectral-SemiBold",
            }}
          >
            Your Selected Pharmacy
          </h3>
          <div
            style={{
              fontSize: "18px",
              padding: "16px",
              border: `1px solid #ccc`,
              borderRadius: "8px",
              margin: "0 16px",
              minWidth: "280px",
            }}
          >
            <div style={{ marginBottom: "6px", fontWeight: "bold" }}>
              {this.state.pharmacyName}
            </div>
            <div style={{ marginBottom: "5px" }}>
              {`${this.state.pharmacyAddress1} ${this.state.pharmacyAddress2}`.trim()}
            </div>
            <div
              style={{ marginBottom: "5px" }}
            >{`${this.state.pharmacyCity}, ${this.state.pharmacyState} ${this.state.pharmacyZip}`}</div>
            <div style={{ marginBottom: "5px" }}>
              {formatPhoneNumber(this.state.pharmacyPhone)}
            </div>
          </div>
          <h4
            style={{
              fontFamily: "Spectral-SemiBold",
              marginTop: "44px",
              marginBottom: "6px",
              fontSize: "23px",
            }}
          >
            Want a different pharmacy?
          </h4>
        </div>
      )
    );
  }

  render() {
    const { classes } = this.props;

    return (
      <div
        className={`${classes.container} select-pharmacy`}
        style={{ backgroundColor: COLOR_KICK_BLUE }}
      >
        {this.state.loadingSearchResults && (
          <div
            style={{
              position: "absolute",
              height: "100%",
              width: "100%",
              backgroundColor: "rgba(0,0,0,0.2)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress style={{ color: kickBlue }} />
          </div>
        )}
        <GridContainer justifyContent="center">
          <GridItem xs={10} sm={10} md={8}>
            <h3
              style={{
                color: "#FFFFFF",
                fontFamily: "Spectral-SemiBold",
                fontSize: "32px",
                marginTop: "40px",
              }}
            >
              Select Pharmacy
            </h3>
          </GridItem>
          <GridItem xs={12} sm={12} md={8}>
            {this.renderUseKickPharmacy()}
            {this.renderDifferentPharmacy()}
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  async componentDidMount() {
    // Check for existing completed visits and redirect to portal if needed
    await checkAndRedirectToFollowUpIfNeeded(
      this.props.client,
      this.state.consultationType,
      this.state.checkoutSessionID,
      { redirectToPortal: true },
    );

    this.visitIDCheckPoller = setInterval(() => {
      if (!getVisitID(this.state.checkoutSessionID)) {
        window.location.href = "/me/portal";
      }
    }, 1000);
  }

  componentWillUnmount() {
    clearInterval(this.visitIDCheckPoller);
  }
}

SelectPharmacy.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withApollo(withStyles(consultationStyles)(SelectPharmacy));
